import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, TrendingDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function ABTestStats({ agentId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/agents/${agentId}/ab-test-stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load A/B test stats');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (isOpen) => {
    setOpen(isOpen);
    if (isOpen) {
      loadStats();
    }
  };

  const getComparison = () => {
    if (!stats) return null;
    const diff = stats.variant_b.in_scope_rate - stats.variant_a.in_scope_rate;
    if (Math.abs(diff) < 1) return { text: 'Similar performance', color: 'text-slate-600', icon: null };
    if (diff > 0) return { text: `Variant B is ${diff.toFixed(1)}% better`, color: 'text-green-600', icon: TrendingUp };
    return { text: `Variant A is ${Math.abs(diff).toFixed(1)}% better`, color: 'text-green-600', icon: TrendingUp };
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full" data-testid="ab-test-button">
          <BarChart3 className="w-4 h-4 mr-2" />
          A/B Test Stats
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Prompt A/B Testing Results</DialogTitle>
          <DialogDescription>
            Compare performance between Variant A (Guardrails First) and Variant B (Context First)
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : stats ? (
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">Currently Using</p>
                <Badge variant="default" className="mt-1">
                  Variant {stats.current_variant}
                </Badge>
              </div>
              {getComparison() && (
                <div className="flex items-center space-x-2">
                  {getComparison().icon && React.createElement(getComparison().icon, { className: `w-5 h-5 ${getComparison().color}` })}
                  <span className={`font-medium ${getComparison().color}`}>
                    {getComparison().text}
                  </span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Variant A */}
              <Card className={stats.current_variant === 'A' ? 'border-primary border-2' : ''}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Variant A</span>
                    <Badge variant="secondary">Guardrails First</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Messages</p>
                    <p className="text-2xl font-bold text-foreground">{stats.variant_a.total_messages}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground">In Scope</p>
                      <p className="text-lg font-semibold text-green-600">{stats.variant_a.in_scope}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Out of Scope</p>
                      <p className="text-lg font-semibold text-orange-600">{stats.variant_a.out_of_scope}</p>
                    </div>
                  </div>
                  <div className="pt-2 border-t">
                    <p className="text-xs text-muted-foreground">In-Scope Rate</p>
                    <p className="text-xl font-bold text-primary">{stats.variant_a.in_scope_rate}%</p>
                  </div>
                </CardContent>
              </Card>

              {/* Variant B */}
              <Card className={stats.current_variant === 'B' ? 'border-primary border-2' : ''}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Variant B</span>
                    <Badge variant="secondary">Context First</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Messages</p>
                    <p className="text-2xl font-bold text-foreground">{stats.variant_b.total_messages}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground">In Scope</p>
                      <p className="text-lg font-semibold text-green-600">{stats.variant_b.in_scope}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Out of Scope</p>
                      <p className="text-lg font-semibold text-orange-600">{stats.variant_b.out_of_scope}</p>
                    </div>
                  </div>
                  <div className="pt-2 border-t">
                    <p className="text-xs text-muted-foreground">In-Scope Rate</p>
                    <p className="text-xl font-bold text-primary">{stats.variant_b.in_scope_rate}%</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                <strong>How it works:</strong> Each message is tracked with its variant. The in-scope rate
                shows how often users ask business-relevant questions vs out-of-scope topics.
                Higher in-scope rate = better prompt ordering for your use case.
              </p>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
